// Libraries
import React from 'react';

// Supermove
import {DataLoader, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useQuery,
  useToast,
  useUrlFilters,
  useEffect,
  useState,
  useMountEffect,
} from '@supermove/hooks';
import {UserModel} from '@supermove/models';
import Datetime, {MomentType} from '@supermove/utils/src/Datetime';

// App
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import UserCalendar from '@shared/modules/UserCalendar/components/UserCalendar';
import {
  getTimeRangeForViewType,
  USER_CALENDAR_VIEW_TYPE,
} from '@shared/modules/UserCalendar/enums/UserCalendarViewType';
import UserCalendarEventForm from '@shared/modules/UserCalendar/forms/UserCalendarEventForm';
import useCreateUserCalendarEventMutation from '@shared/modules/UserCalendar/hooks/useCreateUserCalendarEventMutation';
import UserSettingsPageWrapper from 'modules/User/Settings/components/UserSettingsPageWrapper';

const Content = Styled.View`
  flex: 1;
  max-height: 800px;
`;

const ViewUserCalendarContent = ({
  user,
  calendarId,
  startDate,
  viewType,
  handleViewChange,
  handleDateChange,
  refetch,
}: {
  user: UserModel;
  calendarId: string;
  startDate: Date;
  viewType: USER_CALENDAR_VIEW_TYPE;
  handleViewChange: (viewType: USER_CALENDAR_VIEW_TYPE) => void;
  handleDateChange: (baseDate: Date) => void;
  refetch: () => void;
}) => {
  const createDocumentFlowSuccessToast = useToast({
    ToastComponent: SuccessToast,
  });

  const userCalendarEventForm = UserCalendarEventForm.new(user.id, calendarId);
  const {form, submitting, handleSubmit} = useCreateUserCalendarEventMutation({
    userCalendarEventForm,
    onSuccess: ({userCalendarEvent}) => {
      createDocumentFlowSuccessToast.handleToast({
        message: `'${userCalendarEvent.title}' created!`,
      });
      refetch();
    },
    onError: (errors) => console.log({errors}),
  });

  const handleSelectSlot = (slotInfo: {
    start: Date | string;
    end: Date | string;
    action: 'select' | 'click' | 'doubleClick';
  }) => {
    handleSubmit({
      variables: {
        userCalendarEventForm: {
          ...userCalendarEventForm,
          // Have to cast to Date because BigCalendar wants to have Date | string for dates, but actually passes in Date
          startTime: slotInfo.start as Date,
          endTime: slotInfo.end as Date,
          title: 'New Event',
        },
      },
    });
  };

  return (
    <Content>
      <UserCalendar
        events={user.calendarEvents}
        startDate={startDate}
        viewType={viewType}
        views={Object.values(USER_CALENDAR_VIEW_TYPE)}
        handleDateChange={handleDateChange}
        handleViewChange={handleViewChange}
        handleSelectSlot={handleSelectSlot}
      />
    </Content>
  );
};

type ViewUserCalendarUrlFiltersType = {
  startDate?: string;
  endDate?: string;
  viewType?: USER_CALENDAR_VIEW_TYPE;
  calendarId?: string;
};

const ViewUserCalendarPage = () => {
  const urlFilters = useUrlFilters<ViewUserCalendarUrlFiltersType>({
    getRoute: () => `/account/scheduling/calendars/view`,
    filterKeys: ['startDate', 'endDate', 'viewType', 'calendarId'],
  });

  const viewTypeParam = urlFilters.get('viewType') as USER_CALENDAR_VIEW_TYPE | undefined;
  const [viewType, setViewType] = useState<USER_CALENDAR_VIEW_TYPE>(
    viewTypeParam || USER_CALENDAR_VIEW_TYPE.MONTH,
  );

  const calendarId = urlFilters.get('calendarId');
  const startDateParam = urlFilters.get('startDate');
  const endDateParam = urlFilters.get('endDate');

  // Get default time range based on view type if not specified in URL
  const timeRange = getTimeRangeForViewType(viewType);
  const startDate: MomentType = startDateParam
    ? Datetime.fromDatetime(startDateParam)
    : timeRange.startDate;
  const endDate: MomentType = endDateParam
    ? Datetime.fromDatetime(endDateParam)
    : timeRange.endDate;

  const [isInitialized, setIsInitialized] = useState(false);

  // Set default URL parameters on mount if they're not already set
  useMountEffect(() => {
    if (!isInitialized) {
      const paramsToUpdate: ViewUserCalendarUrlFiltersType = {};

      if (!viewTypeParam) {
        paramsToUpdate.viewType = viewType;
      }

      if (!startDateParam) {
        paramsToUpdate.startDate = timeRange.startDate.toISOString();
      }

      if (!endDateParam) {
        paramsToUpdate.endDate = timeRange.endDate.toISOString();
      }

      // Only update URL if any parameters need to be set
      if (Object.keys(paramsToUpdate).length > 0) {
        urlFilters.handleUpdate(paramsToUpdate);
      }

      setIsInitialized(true);
    }
  });

  const handleViewTypeChange = (newViewType: USER_CALENDAR_VIEW_TYPE) => {
    setViewType(newViewType);
    // Update URL filters with new view type and time range
    const newTimeRange = getTimeRangeForViewType(newViewType);
    urlFilters.handleUpdate({
      viewType: newViewType,
      startDate: newTimeRange.startDate.toISOString(),
      endDate: newTimeRange.endDate.toISOString(),
    });
  };

  // Update navigation with a new date
  const handleDateChange = (newBaseDate: Date) => {
    const newTimeRange = getTimeRangeForViewType(viewType, Datetime.new(newBaseDate));
    urlFilters.handleUpdate({
      startDate: newTimeRange.startDate.toISOString(),
      endDate: newTimeRange.endDate.toISOString(),
    });
  };

  const {loading, data, refetch} = useQuery<{viewer: UserModel}>(ViewUserCalendarPage.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      calendarId,
      startDate,
      endDate,
    },
    skip: !isInitialized || !calendarId,
  });

  useEffect(() => {
    refetch();
  }, [startDateParam, endDateParam, viewType, isInitialized]);

  return (
    <UserSettingsPageWrapper selectedLabel={'Scheduling/Calendar'} pageTitle={'Calendar'}>
      <DataLoader loading={loading || !isInitialized || !calendarId} data={data}>
        {({loadedData}) => (
          <ViewUserCalendarContent
            user={loadedData.viewer}
            calendarId={calendarId!}
            startDate={startDate.toDate()}
            viewType={viewType}
            handleViewChange={handleViewTypeChange}
            handleDateChange={handleDateChange}
            refetch={refetch}
          />
        )}
      </DataLoader>
    </UserSettingsPageWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ViewUserCalendarPage.query = gql`
  ${UserCalendar.fragment}
  
  query ViewUserCalendarPage($calendarId: String!, $startDate: String!, $endDate: String!) {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
      }
      calendar(calendarId: $calendarId) {
        id
        name
      }
      ...UserCalendar
    }
  }
`;

export default ViewUserCalendarPage;
